import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, User, Users, ShieldCheck, Star, Mail, Check, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { chatApi, apiError } from "../lib/api";
import { ChatThread } from "../components/ChatThread";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ChatStaff() {
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [showStart, setShowStart] = useState(false);
  const [showFamily, setShowFamily] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [privacy, setPrivacy] = useState({ text: "", kind: "direct", members: [] });
  const [family, setFamily] = useState({ members: [], invites: [] });
  const [selected, setSelected] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [survey, setSurvey] = useState(null);

  const load = useCallback(async () => {
    try { setConvs(await chatApi.conversations()); } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, [load]);

  const openConv = async (id) => {
    try {
      const c = await chatApi.conversation(id);
      setActive(c);
      if (c.status === "closed" && !c.survey) setSurvey(c);
    } catch (e) { toast.error(apiError(e)); }
  };

  const startDirect = async () => {
    const { privacy_text } = await chatApi.intro();
    setPrivacy({ text: privacy_text, kind: "direct", members: [] });
    setShowStart(false);
    setShowPrivacy(true);
  };

  const startFamily = async () => {
    setShowStart(false);
    try { setFamily(await chatApi.family()); } catch (e) { toast.error(apiError(e)); }
    setSelected([]);
    setShowFamily(true);
  };

  const confirmFamily = async () => {
    if (selected.length === 0) { toast.error("Selecciona al menos un familiar."); return; }
    const { privacy_text } = await chatApi.intro();
    setPrivacy({ text: privacy_text, kind: "family", members: selected });
    setShowFamily(false);
    setShowPrivacy(true);
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await chatApi.inviteFamily(inviteEmail.trim());
      toast.success("Invitación enviada. Tu familiar debe aceptarla para unirse.");
      setInviteEmail("");
      setFamily(await chatApi.family());
    } catch (e) { toast.error(apiError(e)); }
    finally { setInviting(false); }
  };

  const respondInvite = async (id, accept) => {
    try {
      if (accept) { await chatApi.acceptInvite(id); toast.success("Te uniste al grupo familiar."); }
      else { await chatApi.rejectInvite(id); toast.success("Invitación rechazada."); }
      setFamily(await chatApi.family());
    } catch (e) { toast.error(apiError(e)); }
  };

  const acceptPrivacy = async () => {
    try {
      const c = await chatApi.createConversation({ kind: privacy.kind, member_ids: privacy.members, privacy_accepted: true });
      setShowPrivacy(false);
      await load();
      openConv(c.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  // ---- Conversation open view ----
  if (active) {
    return (
      <div className="h-screen flex flex-col" data-testid="chat-conversation-view">
        <div className="bg-navy flex-shrink-0 px-4 h-14 flex items-center">
          <button data-testid="conv-back-btn" onClick={() => { setActive(null); load(); }} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold"><ArrowLeft className="w-4 h-4" /> Mis chats</button>
        </div>
        <div className="flex-1 min-h-0">
          <ChatThread conversation={active} ctx={chatApi} allowCopy={false}
            onUpdated={(c) => { setActive(c); if (c.status === "closed" && !c.survey) setSurvey(c); }} />
        </div>
        {survey && <SurveyModal conv={survey} onClose={() => setSurvey(null)} />}
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="min-h-screen bg-cream" data-testid="chat-staff-page">
      <header className="bg-navy">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between">
          <button data-testid="staff-back-btn" onClick={() => navigate("/chat")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold"><ArrowLeft className="w-4 h-4" /> Volver</button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-extrabold text-[#0A1C30] flex items-center gap-2"><MessageSquareText className="w-6 h-6 text-gold" /> Chat con staff Supera</h1>
          <button data-testid="new-chat-btn" onClick={() => setShowStart(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-4 py-2.5 text-sm"><Plus className="w-4 h-4" /> Nuevo chat</button>
        </div>

        <div className="mt-6 space-y-3" data-testid="conv-list">
          {convs.length === 0 && (
            <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center">
              <MessageSquareText className="w-10 h-10 text-[#C7CDD6] mx-auto" />
              <p className="mt-3 text-[#6B7688] font-semibold">Aún no tienes conversaciones. Inicia un nuevo chat con el staff.</p>
            </div>
          )}
          {convs.map((c) => (
            <button key={c.id} data-testid={`conv-item-${c.id}`} onClick={() => openConv(c.id)} className="w-full text-left bg-white rounded-2xl ring-1 ring-black/5 p-4 flex items-center gap-3 hover:shadow-md transition-all">
              <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center flex-shrink-0">{c.kind === "family" ? <Users className="w-5 h-5 text-gold" /> : <User className="w-5 h-5 text-gold" />}</span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="font-extrabold text-[#0A1C30]">{c.kind === "family" ? "Grupo familiar" : "Chat con staff"}</span>
                  {c.status === "waiting" && <span className="text-[10px] font-bold text-[#E9A400] bg-[#FBF0CE] px-2 py-0.5 rounded-full">Esperando</span>}
                  {c.status === "active" && <span className="text-[10px] font-bold text-[#22A860] bg-[#E2F6EB] px-2 py-0.5 rounded-full">Activo</span>}
                  {c.status === "closed" && <span className="text-[10px] font-bold text-[#6B7688] bg-[#EDF0F4] px-2 py-0.5 rounded-full">Finalizado</span>}
                </span>
                <span className="block text-sm text-[#6B7688] truncate">{c.last_message_preview || "Sin mensajes"}</span>
              </span>
              {c.unread > 0 && <span className="w-2.5 h-2.5 rounded-full bg-[#E23B3B] flex-shrink-0" data-testid="conv-unread-dot" />}
            </button>
          ))}
        </div>
      </div>

      {/* Start options */}
      <Dialog open={showStart} onOpenChange={setShowStart}>
        <DialogContent data-testid="start-dialog">
          <DialogHeader><DialogTitle>¿Cómo deseas chatear?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <button data-testid="start-direct" onClick={startDirect} className="w-full flex items-center gap-3 rounded-2xl ring-1 ring-black/10 p-4 hover:bg-[#FAF8F1] transition-colors text-left">
              <span className="w-11 h-11 rounded-xl bg-[#0A1C30] flex items-center justify-center"><User className="w-6 h-6 text-gold" /></span>
              <span><span className="block font-extrabold text-[#0A1C30]">Chatear uno a uno</span><span className="block text-sm text-[#6B7688]">Con un asesor del staff Supera disponible.</span></span>
            </button>
            <button data-testid="start-family" onClick={startFamily} className="w-full flex items-center gap-3 rounded-2xl ring-1 ring-black/10 p-4 hover:bg-[#FAF8F1] transition-colors text-left">
              <span className="w-11 h-11 rounded-xl bg-[var(--gold)] flex items-center justify-center"><Users className="w-6 h-6 text-[#0A1C30]" /></span>
              <span><span className="block font-extrabold text-[#0A1C30]">Chatear en grupo con mi familia</span><span className="block text-sm text-[#6B7688]">Une a familiares registrados + un asesor del staff.</span></span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Family selection */}
      <Dialog open={showFamily} onOpenChange={setShowFamily}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="family-dialog">
          <DialogHeader><DialogTitle>Agregar familiares al grupo</DialogTitle></DialogHeader>
          <div className="rounded-xl bg-[#FBF0CE] text-[#8A6D0B] text-xs font-semibold px-3 py-2">
            ⚠️ Solo pueden unirse familiares con TU MISMA Referencia Supera y que ACEPTEN tu invitación. No se permiten usuarios externos.
          </div>
          {family.pending_received?.length > 0 && (
            <div className="mt-2 rounded-xl ring-1 ring-[#22A860]/30 bg-[#E2F6EB]/40 p-3" data-testid="pending-invites">
              <p className="font-bold text-xs text-[#0A1C30] mb-1.5">Invitaciones que recibiste</p>
              {family.pending_received.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-[#2A3446]">{p.inviter_name} <span className="text-xs text-[#6B7688]">({p.inviter_email})</span></span>
                  <span className="flex gap-1.5">
                    <button data-testid={`accept-invite-${p.id}`} onClick={() => respondInvite(p.id, true)} className="rounded-lg bg-[#22A860] text-white font-bold px-2.5 py-1 text-xs">Aceptar</button>
                    <button data-testid={`reject-invite-${p.id}`} onClick={() => respondInvite(p.id, false)} className="rounded-lg bg-[#FDECEC] text-[#C0392B] font-bold px-2.5 py-1 text-xs">Rechazar</button>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-2">
            <Label className="mb-1 block font-bold text-xs">Invitar familiar por correo (misma referencia, debe estar registrado)</Label>
            <div className="flex gap-2">
              <Input data-testid="family-invite-email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="familiar@correo.com" />
              <button data-testid="family-invite-btn" onClick={invite} disabled={inviting} className="rounded-xl bg-navy text-white font-bold px-4 text-sm inline-flex items-center gap-1 disabled:opacity-60"><Mail className="w-4 h-4" /> Invitar</button>
            </div>
          </div>
          <div className="mt-3">
            <Label className="mb-1 block font-bold text-xs">Selecciona quién se une</Label>
            {family.members.length === 0 ? (
              <p className="text-sm text-[#6B7688] py-3">No hay familiares vinculados aún. Invita a uno por correo arriba.</p>
            ) : (
              <div className="space-y-2">
                {family.members.map((m) => (
                  <label key={m.id} data-testid={`family-member-${m.id}`} className="flex items-center gap-3 rounded-xl ring-1 ring-black/5 p-3 cursor-pointer hover:bg-[#FAF8F1]">
                    <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggleSel(m.id)} className="w-4 h-4 accent-[#E9A400]" />
                    <span className="w-9 h-9 rounded-full bg-navy text-gold flex items-center justify-center font-extrabold text-sm">{(m.nombre || "?")[0]}</span>
                    <span><span className="block font-bold text-[#0A1C30] text-sm">{m.nombre}</span><span className="block text-xs text-[#6B7688]">{m.programa || m.email}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><button data-testid="family-continue" onClick={confirmFamily} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5">Continuar</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Privacy notice */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent data-testid="privacy-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#22A860]" /> Aviso de privacidad</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2A3446] leading-relaxed max-h-60 overflow-y-auto">{privacy.text}</p>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowPrivacy(false)} className="rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-5 py-2.5">Cancelar</button>
            <button data-testid="privacy-accept" onClick={acceptPrivacy} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5 inline-flex items-center gap-1"><Check className="w-4 h-4" /> Acepto e inicio</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {survey && <SurveyModal conv={survey} onClose={() => setSurvey(null)} />}
    </div>
  );
}

function SurveyModal({ conv, onClose }) {
  const [r, setR] = useState({ calidad: 0, atencion: 0, informacion: 0 });
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);
  const rows = [["calidad", "Calidad del servicio"], ["atencion", "Atención recibida"], ["informacion", "Información brindada"]];
  const submit = async () => {
    if (!r.calidad || !r.atencion || !r.informacion) { toast.error("Califica las 3 categorías."); return; }
    setSaving(true);
    try { await chatApi.survey(conv.id, { ...r, comentario }); toast.success("¡Gracias por tu evaluación!"); onClose(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="survey-modal">
        <DialogHeader><DialogTitle>¿Cómo fue tu atención?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7688]">Ayúdanos a mejorar evaluando al staff Supera.</p>
        <div className="space-y-4 mt-2">
          {rows.map(([k, label]) => (
            <div key={k}>
              <p className="text-sm font-bold text-[#0A1C30] mb-1">{label}</p>
              <div className="flex gap-1" data-testid={`survey-${k}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setR((s) => ({ ...s, [k]: n }))} data-testid={`survey-${k}-${n}`}>
                    <Star className={`w-7 h-7 ${n <= r[k] ? "fill-[#E9A400] text-[#E9A400]" : "text-[#D0D5DD]"}`} />
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <Label className="mb-1 block font-bold text-xs">Comentario (opcional)</Label>
            <textarea data-testid="survey-comment" value={comentario} onChange={(e) => setComentario(e.target.value)} className="w-full min-h-[80px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-5 py-2.5">Ahora no</button>
          <button data-testid="survey-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5 disabled:opacity-60">Enviar evaluación</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
